import * as React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon     from "@mui/icons-material/Download";
import OpenInNewIcon    from "@mui/icons-material/OpenInNew";

/**
 * PdfPreview — iframe with stable src to prevent flickering.
 * onDownload and onOpenNewTab are ALWAYS rendered (not hidden when locked).
 */
export function PdfPreview({ url, color, onDownload, onOpenNewTab }) {
  // Keep a stable ref to the URL — only update when url actually changes
  // This prevents iframe from re-mounting on every parent render
  const stableUrl = React.useRef(url);
  React.useEffect(() => {
    if (url && url !== stableUrl.current) {
      stableUrl.current = url;
    }
  }, [url]);

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }
    // Fallback: direct link if no handler provided
    if (url) {
      const a = document.createElement("a");
      a.href     = url;
      a.download = "bill.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleOpenNewTab = () => {
    if (onOpenNewTab) {
      onOpenNewTab();
      return;
    }
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: `2px solid ${color}`,
        mb: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Toolbar — always shown */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.2,
          backgroundColor: color,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PictureAsPdfIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, fontSize: "0.85rem" }}>
            PDF Bill Preview
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownload}
            sx={{
              backgroundColor: "rgba(255,255,255,0.22)", color: "#fff",
              fontWeight: 700, fontSize: "0.72rem", textTransform: "none",
              borderRadius: 2, px: 1.5, py: 0.4,
              border: "1px solid rgba(255,255,255,0.35)",
              minWidth: 0,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.38)" },
            }}
          >
            Download
          </Button>
          <Button
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
            onClick={handleOpenNewTab}
            sx={{
              backgroundColor: "rgba(255,255,255,0.22)", color: "#fff",
              fontWeight: 700, fontSize: "0.72rem", textTransform: "none",
              borderRadius: 2, px: 1.5, py: 0.4,
              border: "1px solid rgba(255,255,255,0.35)",
              minWidth: 0,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.38)" },
            }}
          >
            Open
          </Button>
        </Stack>
      </Box>

      {/* 
        iframe key is intentionally FIXED ("pdf-preview") so React never
        re-mounts it on re-renders — this eliminates the flicker completely.
        The src is set once; if url changes we use a ref to update via imperative DOM.
      */}
      <Box
        key="pdf-preview"
        component="iframe"
        src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
        title="PDF Preview"
        sx={{
          width: "100%",
          height: 430,
          border: "none",
          display: "block",
          backgroundColor: "#ffffff",
        }}
      />
    </Box>
  );
}

export function ImagePreviewBlock({ url, color }) {
  return (
    <Paper sx={{ borderRadius: 3, overflow: "hidden", border: `2px solid ${color}`, mb: 2 }}>
      <Box
        component="img"
        src={url}
        alt="Bill Preview"
        sx={{
          width: "100%",
          maxHeight: 400,
          objectFit: "contain",
          backgroundColor: "#f8fafc",
          display: "block",
        }}
      />
    </Paper>
  );
}